import {
  Alert,
  Button,
  Center,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import {
  addendumService,
  type AddendumDraft,
} from "../../services/addendum.service";
import type {
  ReportContentVersion,
  ReportPreviewEvidence,
} from "../../services/signOff.service";
import { ReportPreviewModal } from "../workflows/ReportPreviewModal";
import type { DocumentViewResponse } from "../../types/reportTemplate";

type ExternalBaselineDocumentView = DocumentViewResponse & {
  reviewContext?: { externalBaselineId?: string };
};

interface AddendumReportPreviewModalProps {
  opened: boolean;
  caseId: string;
  draft: AddendumDraft;
  onClose: () => void;
  onSignOut?: (evidence?: ReportPreviewEvidence) => void | Promise<void>;
  signOutPending?: boolean;
}

export function AddendumReportPreviewModal({
  opened,
  caseId,
  draft,
  onClose,
  onSignOut,
  signOutPending,
}: AddendumReportPreviewModalProps) {
  const [refreshFailed, setRefreshFailed] = useState(false);
  const previewQuery = useQuery({
    queryKey: [
      "case",
      caseId,
      "addendum-draft",
      draft.id,
      "sign-out-preview",
      draft.revision,
    ],
    queryFn: () =>
      addendumService.getSignOutPreview(caseId, draft.id, draft.revision),
    enabled: opened,
  });
  const preview = previewQuery.data;
  const externalBaselineId = (
    preview?.documentViewData as ExternalBaselineDocumentView | undefined
  )?.reviewContext?.externalBaselineId;
  const eligibilityQuery = useQuery({
    queryKey: ["case", caseId, "addendum-eligibility", externalBaselineId],
    queryFn: () => addendumService.getEligibility(caseId),
    enabled: opened && Boolean(externalBaselineId),
  });
  const externalBaselineUnavailable = Boolean(
    externalBaselineId &&
      (eligibilityQuery.isError ||
        (eligibilityQuery.data &&
          (eligibilityQuery.data.baselineId !== externalBaselineId ||
            !eligibilityQuery.data.downloadUrl)))
  );
  const externalBaselineLoading = Boolean(
    externalBaselineId && !eligibilityQuery.data && !eligibilityQuery.isError
  );
  const interstitialReportFile = useMemo(
    () =>
      externalBaselineId &&
      eligibilityQuery.data?.baselineId === externalBaselineId &&
      eligibilityQuery.data.downloadUrl
        ? {
            file: {
              id: externalBaselineId,
              name: "Castle Biosciences Report.pdf",
              mimeType: "application/pdf",
              label: "Castle Biosciences Report",
            },
            url: eligibilityQuery.data.downloadUrl,
          }
        : undefined,
    [eligibilityQuery.data, externalBaselineId]
  );
  const previewData = useMemo(
    () =>
      preview
        ? {
            ...preview.previewData,
            review: preview.review,
          }
        : undefined,
    [preview]
  );
  const handleAcknowledgeReview = useCallback(
    (contentVersion: ReportContentVersion) =>
      addendumService.acknowledgeSignOutPreview(
        caseId,
        draft.id,
        draft.revision,
        contentVersion
      ),
    [caseId, draft.id, draft.revision]
  );
  const handleReviewContentChanged = useCallback(async () => {
    const [previewResult, eligibilityResult] = await Promise.all([
      previewQuery.refetch(),
      externalBaselineId ? eligibilityQuery.refetch() : Promise.resolve(null),
    ]);
    setRefreshFailed(
      previewResult.isError || Boolean(eligibilityResult?.isError)
    );
  }, [eligibilityQuery, externalBaselineId, previewQuery]);
  const handleRetry = useCallback(async () => {
    const [previewResult, eligibilityResult] = await Promise.all([
      previewQuery.refetch(),
      externalBaselineId ? eligibilityQuery.refetch() : Promise.resolve(null),
    ]);
    setRefreshFailed(
      previewResult.isError || Boolean(eligibilityResult?.isError)
    );
  }, [eligibilityQuery, externalBaselineId, previewQuery]);
  const handleSignOut = useCallback(
    (evidence?: ReportPreviewEvidence) => {
      return onSignOut?.(evidence);
    },
    [onSignOut]
  );

  if (
    !preview ||
    refreshFailed ||
    externalBaselineLoading ||
    externalBaselineUnavailable
  ) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title="Addendum Preview"
        size="lg"
        centered
        closeButtonProps={{ "aria-label": "Close addendum preview" }}
      >
        {previewQuery.isError ||
        refreshFailed ||
        externalBaselineUnavailable ? (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            title="Addendum preview unavailable"
          >
            <Stack gap="sm" align="flex-start">
              <Text size="sm">
                The Castle report and addendum could not be loaded together.
                Retry before signing out.
              </Text>
              <Button variant="light" color="red" onClick={handleRetry}>
                Retry Preview
              </Button>
            </Stack>
          </Alert>
        ) : (
          <Center py="xl">
            <Loader />
          </Center>
        )}
      </Modal>
    );
  }

  return (
    <ReportPreviewModal
      key={preview.review.contentVersion.fingerprint}
      opened={opened}
      onClose={onClose}
      caseId={caseId}
      title="Addendum Preview"
      reportVersion={preview.baseReportVersion}
      forceTemplateId={preview.templateId ?? undefined}
      documentViewDataOverride={preview.documentViewData}
      previewDataOverride={previewData}
      signedDocumentViewData={preview.baseDocumentViewData}
      interstitialReportFile={interstitialReportFile}
      reviewRequired={preview.review.reviewRequired}
      onAcknowledgeReview={handleAcknowledgeReview}
      onReviewContentChanged={handleReviewContentChanged}
      onSignOut={onSignOut ? handleSignOut : undefined}
      signOutPending={signOutPending}
      allowSignOutWhenFinalized
      simulateMainSignature={false}
      showPrintAction={false}
      signOutLabel={
        preview.review.reviewRequired
          ? "Sign Out Reviewed Addendum"
          : "Sign Out Addendum"
      }
      readOnly={!onSignOut}
    />
  );
}
